import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import {
  FIRST_DEPOSIT_REWARD_CASH,
  FIRST_DEPOSIT_UNLOCK_SCORE,
  REFERRAL_UNLOCK_SCORE,
} from "../utils/referralsHelpers";

const referralApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

referralApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fetchMyReferrals = createAsyncThunk(
  "referrals/fetchMyReferrals",
  async ({ page = 1, limit = 20, status, append = false } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("limit", String(limit));
      if (status) params.append("status", status);

      const response = await referralApi.get(`/api/referrals/me?${params.toString()}`);
      return { ...response.data, append, requestedPage: page };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load referrals"
      );
    }
  }
);

export const regenerateInviteCode = createAsyncThunk(
  "referrals/regenerateInviteCode",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await referralApi.post(`/api/profile/${userId}/inviteCode`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to generate invite code"
      );
    }
  }
);

export const claimReferralReward = createAsyncThunk(
  "referrals/claimReferralReward",
  async (referralId, { rejectWithValue }) => {
    try {
      const response = await referralApi.post(`/api/referrals/${referralId}/claim`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to claim referral reward"
      );
    }
  }
);

export const claimFirstDepositReward = createAsyncThunk(
  "referrals/claimFirstDepositReward",
  async (_, { rejectWithValue }) => {
    try {
      const response = await referralApi.post("/api/referrals/me/first-deposit/claim");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to claim first deposit bonus"
      );
    }
  }
);

const emptyFirstDepositReward = {
  amount: FIRST_DEPOSIT_REWARD_CASH,
  unlockScoreThreshold: FIRST_DEPOSIT_UNLOCK_SCORE,
  status: "awaiting_deposit",
  canClaim: false,
  scoreRequirementMet: false,
  allTimeScores: 0,
  progressPercent: 0,
  scoreRemaining: FIRST_DEPOSIT_UNLOCK_SCORE,
  firstDepositAt: null,
  depositAmount: 0,
  claimedAt: null,
};

const initialState = {
  inviteCode: null,
  unlockScoreThreshold: REFERRAL_UNLOCK_SCORE,
  rewardAmount: 1000,
  firstDepositRewardAmount: FIRST_DEPOSIT_REWARD_CASH,
  firstDepositUnlockScore: FIRST_DEPOSIT_UNLOCK_SCORE,
  firstDepositReward: emptyFirstDepositReward,
  referrals: [],
  totals: {
    totalReferrals: 0,
    pendingCount: 0,
    rewardedCount: 0,
    cancelledCount: 0,
    pendingCash: 0,
    earnedCash: 0,
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  loadingMore: false,
  refreshing: false,
  regeneratingInviteCode: false,
  regenerateInviteCodeError: null,
  claimingReferralId: null,
  claimingFirstDeposit: false,
  claimError: null,
  error: null,
  lastFetchedAt: null,
};

const applyClaimedReferral = (state, referral) => {
  if (!referral?.id) return;
  const previous = state.referrals.find((item) => String(item.id) === String(referral.id));
  state.referrals = state.referrals.map((item) =>
    String(item.id) === String(referral.id) ? referral : item
  );
  if (previous?.status === "pending" && referral.status === "rewarded") {
    const amount = Number(referral.rewardAmount) || 0;
    state.totals.pendingCount = Math.max(0, (state.totals.pendingCount || 0) - 1);
    state.totals.rewardedCount = (state.totals.rewardedCount || 0) + 1;
    state.totals.pendingCash = Math.max(0, (state.totals.pendingCash || 0) - amount);
    state.totals.earnedCash = (state.totals.earnedCash || 0) + amount;
  }
};

const referralSlice = createSlice({
  name: "referrals",
  initialState,
  reducers: {
    clearReferralError: (state) => {
      state.error = null;
      state.claimError = null;
    },
    clearRegenerateInviteCodeError: (state) => {
      state.regenerateInviteCodeError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyReferrals.pending, (state, action) => {
        const { append, page } = action.meta.arg || {};
        if (append || page > 1) {
          state.loadingMore = true;
        } else if (state.referrals.length > 0) {
          state.refreshing = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMyReferrals.fulfilled, (state, action) => {
        const { append, requestedPage } = action.payload;
        const incoming = action.payload.referrals || [];

        state.loading = false;
        state.loadingMore = false;
        state.refreshing = false;
        state.inviteCode = action.payload.inviteCode ?? state.inviteCode;
        state.unlockScoreThreshold =
          action.payload.unlockScoreThreshold ?? REFERRAL_UNLOCK_SCORE;
        state.rewardAmount = action.payload.rewardAmount ?? state.rewardAmount;
        state.firstDepositRewardAmount =
          action.payload.firstDepositRewardAmount ?? state.firstDepositRewardAmount;
        state.firstDepositUnlockScore =
          action.payload.firstDepositUnlockScore ?? state.firstDepositUnlockScore;
        state.firstDepositReward =
          action.payload.firstDepositReward || state.firstDepositReward;
        state.totals = action.payload.totals || state.totals;
        state.pagination = action.payload.pagination || state.pagination;
        state.referrals =
          append || (requestedPage && requestedPage > 1)
            ? [...state.referrals, ...incoming]
            : incoming;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchMyReferrals.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.refreshing = false;
        state.error = action.payload || "Failed to load referrals";
      })
      .addCase(regenerateInviteCode.pending, (state) => {
        state.regeneratingInviteCode = true;
        state.regenerateInviteCodeError = null;
      })
      .addCase(regenerateInviteCode.fulfilled, (state, action) => {
        state.regeneratingInviteCode = false;
        state.inviteCode = action.payload.inviteCode ?? state.inviteCode;
      })
      .addCase(regenerateInviteCode.rejected, (state, action) => {
        state.regeneratingInviteCode = false;
        state.regenerateInviteCodeError = action.payload || "Failed to generate invite code";
      })
      .addCase(claimReferralReward.pending, (state, action) => {
        state.claimingReferralId = action.meta.arg || null;
        state.claimError = null;
      })
      .addCase(claimReferralReward.fulfilled, (state, action) => {
        state.claimingReferralId = null;
        applyClaimedReferral(state, action.payload.referral);
      })
      .addCase(claimReferralReward.rejected, (state, action) => {
        state.claimingReferralId = null;
        state.claimError = action.payload || "Failed to claim referral reward";
      })
      .addCase(claimFirstDepositReward.pending, (state) => {
        state.claimingFirstDeposit = true;
        state.claimError = null;
      })
      .addCase(claimFirstDepositReward.fulfilled, (state, action) => {
        state.claimingFirstDeposit = false;
        if (action.payload.firstDepositReward) {
          state.firstDepositReward = action.payload.firstDepositReward;
        }
      })
      .addCase(claimFirstDepositReward.rejected, (state, action) => {
        state.claimingFirstDeposit = false;
        state.claimError = action.payload || "Failed to claim first deposit bonus";
      });
  },
});

export const { clearReferralError, clearRegenerateInviteCodeError } = referralSlice.actions;
export default referralSlice.reducer;
